import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, hasRole } from "../context/AuthContext";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import DataTableBlock from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useNestedBootstrapList } from "../hooks/useBootstrapList";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { STACK_SAMPLE_INTAKE_COLUMNS, STACK_SAMPLE_INTAKE_DEFAULT_SORT } from "./stackSampleIntakeColumns";
import AdminEntityActions from "../components/AdminEntityActions";

const INTAKE_ROLES = ["admin", "manager", "supervisor", "lab", "purchaser"];
const TIMING_OPTIONS = [
  { value: "BEFORE_CONTRACT", label: "Before contract" },
  { value: "AFTER_CONTRACT", label: "After contract" },
];

const emptyStackRow = () => ({ stack_number: "", bags: "", quality: "" });

const statusTone = (s) => {
  if (s === "APPROVED" || s === "LAB_DONE") return "sage";
  if (s === "REJECTED") return "danger";
  if (s === "PENDING_APPROVAL" || s === "PARTIALLY_APPROVED") return "gold";
  return "default";
};

export default function StackSampleIntakePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [filterPartyId, setFilterPartyId] = useState("");
  const sampleListExtraParams = useMemo(
    () => (filterPartyId ? { party_id: filterPartyId } : {}),
    [filterPartyId],
  );
  const sampleList = useNestedBootstrapList({
    path: "/stack-sample-intake/bootstrap",
    listKey: "samples",
    columns: STACK_SAMPLE_INTAKE_COLUMNS,
    defaultSort: STACK_SAMPLE_INTAKE_DEFAULT_SORT,
    extraParams: sampleListExtraParams,
    wsTypes: ["stack"],
  });
  useBootstrapWS(["stack"], sampleList.reload);
  const parties = sampleList.extras.parties || [];
  const allContracts = sampleList.extras.contracts || [];
  const [intakeTiming, setIntakeTiming] = useState("BEFORE_CONTRACT");
  const [partyId, setPartyId] = useState("");
  const [selectedContractIds, setSelectedContractIds] = useState([]);
  const [stackRows, setStackRows] = useState([emptyStackRow()]);
  const [sampleNotes, setSampleNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isBefore = intakeTiming === "BEFORE_CONTRACT";
  const isAfter = intakeTiming === "AFTER_CONTRACT";
  const canCreate = hasRole(user, INTAKE_ROLES);

  const contractsForParty = useMemo(() => {
    if (!partyId) return [];
    return allContracts.filter((c) => String(c.party_id) === String(partyId));
  }, [allContracts, partyId]);

  useEffect(() => {
    const fromUrl = searchParams.get("party_id");
    if (fromUrl) {
      setFilterPartyId(String(fromUrl));
      setPartyId(String(fromUrl));
    }
  }, [searchParams]);

  const toggleContract = (id) => {
    const sid = String(id);
    setSelectedContractIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid],
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!partyId) {
      toast.error("Select a party");
      return;
    }
    const stacksPayload = stackRows
      .map((r) => ({
        stack_number: String(r.stack_number || "").trim(),
        bags: Number(r.bags),
        quality: String(r.quality || "").trim(),
      }))
      .filter((r) => r.stack_number || r.quality || r.bags);

    if (stacksPayload.length < 1) {
      toast.error("Add at least one stack line");
      return;
    }
    for (const row of stacksPayload) {
      if (!row.stack_number || !row.quality || Number.isNaN(row.bags)) {
        toast.error("Each line needs stack number, bags, and quality");
        return;
      }
    }
    if (isAfter && selectedContractIds.length < 1) {
      toast.error("Select at least one purchase contract");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await api.post("/stack-samples", {
        intake_timing: intakeTiming,
        party_id: Number(partyId),
        purchase_contract_ids: isAfter ? selectedContractIds.map(Number) : undefined,
        stacks: stacksPayload,
        notes: sampleNotes,
      });
      const codes = (data.stack_lines || []).map((l) => l.sample_code).join(", ");
      toast.success(`Registered ${data.code}. Sample IDs: ${codes}`);
      setSelectedContractIds([]);
      setStackRows([emptyStackRow()]);
      setSampleNotes("");
      sampleList.reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="stack-sample-intake-page">
      <PageHeader
        eyebrow="Quality · Procurement"
        title="Stack sample intake"
        description="Register purchase stack samples with a unique sample ID per stack. Lab analyses each ID; purchaser approves per stack."
      />


      <SectionCard title="Register new intake">
        {!canCreate ? (
          <p className="text-sm text-muted-foreground">Your role cannot create stack sample intakes.</p>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Timing" required>
                <Select
                  value={intakeTiming}
                  onValueChange={(v) => {
                    setIntakeTiming(v);
                    setSelectedContractIds([]);
                  }}
                >
                  <SelectTrigger data-testid="intake-timing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMING_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Party" required>
                <Select
                  value={partyId || "_none"}
                  onValueChange={(v) => {
                    const id = v === "_none" ? "" : v;
                    setPartyId(id);
                    setSelectedContractIds([]);
                  }}
                >
                  <SelectTrigger data-testid="intake-party">
                    <SelectValue placeholder="Select party…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none" disabled>
                      Select party…
                    </SelectItem>
                    {parties.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.display_name}
                        {p.phone ? ` · ${p.phone}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            {isAfter && partyId && (
              <FormField label="Purchase contract(s)" required>
                <div className="rounded-lg border border-border divide-y max-h-52 overflow-y-auto bg-muted/10">
                  {contractsForParty.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground">No active contracts for this party.</p>
                  ) : (
                    contractsForParty.map((c) => (
                      <label
                        key={c.id}
                        className="flex items-start gap-3 p-3 cursor-pointer hover:bg-background transition-colors"
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedContractIds.includes(String(c.id))}
                          onChange={() => toggleContract(c.id)}
                        />
                        <span className="text-sm">
                          <span className="font-mono text-xs link-brand">{c.contract_number}</span>
                          <span className="text-muted-foreground"> · {c.quality} · {c.quantity} qtl</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </FormField>
            )}

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Stack lines
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setStackRows((rows) => [...rows, emptyStackRow()])}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add line
                </Button>
              </div>
              <div className="space-y-3">
                {stackRows.map((row, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end p-4 rounded-lg border bg-muted/30"
                  >
                    <div className="sm:col-span-3">
                      <FormField label="Stack #" required compact>
                        <Input
                          value={row.stack_number}
                          onChange={(e) =>
                            setStackRows((rows) =>
                              rows.map((r, j) => (j === i ? { ...r, stack_number: e.target.value } : r)),
                            )
                          }
                        />
                      </FormField>
                    </div>
                    <div className="sm:col-span-2">
                      <FormField label="Bags" required compact>
                        <Input
                          type="number"
                          min="0"
                          value={row.bags}
                          onChange={(e) =>
                            setStackRows((rows) =>
                              rows.map((r, j) => (j === i ? { ...r, bags: e.target.value } : r)),
                            )
                          }
                        />
                      </FormField>
                    </div>
                    <div className="sm:col-span-5">
                      <FormField label="Quality" required compact>
                        <Input
                          value={row.quality}
                          onChange={(e) =>
                            setStackRows((rows) =>
                              rows.map((r, j) => (j === i ? { ...r, quality: e.target.value } : r)),
                            )
                          }
                        />
                      </FormField>
                    </div>
                    <div className="sm:col-span-2 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={stackRows.length <= 1}
                        onClick={() =>
                          setStackRows((rows) => (rows.length > 1 ? rows.filter((_, j) => j !== i) : rows))
                        }
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Each line receives a system sample ID (e.g. SS-260523-1234-01).
              </p>
            </div>

            <FormField label="Notes">
              <Input
                value={sampleNotes}
                onChange={(e) => setSampleNotes(e.target.value)}
                placeholder="Vehicle, lot reference…"
              />
            </FormField>

            <div className="flex justify-end pt-2 border-t">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-w-[160px]"
                data-testid="intake-submit"
              >
                {submitting ? "Saving…" : "Register intake"}
              </Button>
            </div>
          </form>
        )}
      </SectionCard>

      <SectionCard
        title="Sample registry"
        action={
          <div className="w-full sm:w-64">
            <Select value={filterPartyId || "all"} onValueChange={(v) => setFilterPartyId(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All parties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All parties</SelectItem>
                {parties.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      >
        {sampleList.loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
        ) : (
          <DataTableBlock
            title=""
            wrapInSectionCard={false}
            columns={STACK_SAMPLE_INTAKE_COLUMNS}
            table={sampleList.table}
            totalCount={sampleList.meta.count}
            testId="stack-sample-registry"
            minWidth="720px"
            showHint={false}
            emptyMessage="No samples for this filter."
            emptyFilteredMessage="No samples match your filters."
            pagination={{ ...sampleList.pagination, pageSizeOptions: LIST_PAGE_SIZE_OPTIONS }}
            exportButton={(
              <DataTableExportButton
                apiPath="/stack-samples"
                params={sampleList.exportParams}
                columns={STACK_SAMPLE_INTAKE_COLUMNS}
                filename="stack-samples.xlsx"
                testId="stack-samples-export"
              />
            )}
            renderRow={(s) => (
              <tr key={s.id} className="border-b last:border-0 hover:bg-accent/60">
                <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                <td className="px-4 py-3">{s.party_name || "—"}</td>
                <td className="px-4 py-3">
                  <Pill tone={s.intake_timing === "AFTER_CONTRACT" ? "sage" : "gold"}>
                    {s.intake_timing === "AFTER_CONTRACT" ? "After" : "Before"}
                  </Pill>
                </td>
                <td className="px-4 py-3">
                  <Pill tone={statusTone(s.status)}>{s.status?.replace(/_/g, " ")}</Pill>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {(s.stack_lines || []).map((ln) => (
                    <div key={ln.id || ln.sample_code} className="py-0.5">
                      <span className="font-mono text-foreground">{ln.sample_code}</span>
                      {" · "}
                      {ln.stack_number} ({ln.bags} bags, {ln.quality})
                      {" — "}
                      <span className="uppercase text-[10px]">{ln.lab_status}</span>
                      {ln.approval_status ? ` / ${ln.approval_status}` : ""}
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3">
                  <AdminEntityActions
                    entityType="stack_sample_intake"
                    entityId={s.id}
                    entityLabel={s.code}
                    onDeleted={sampleList.reload}
                  />
                </td>
              </tr>
            )}
            renderTrailingHeader=""
          />
        )}
      </SectionCard>
    </div>
  );
}

function FormField({ label, required, compact, children }) {
  return (
    <div>
      <Label
        className={
          compact
            ? "text-[10px] uppercase tracking-widest text-muted-foreground"
            : "text-xs font-medium text-foreground"
        }
      >
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <div className={compact ? "mt-1" : "mt-1.5"}>{children}</div>
    </div>
  );
}
